import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Helmet } from 'react-helmet';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const AgeCalculator = () => {
  const [birthdate, setBirthdate] = useState('');
  const [ageYears, setAgeYears] = useState(null);
  const [ageMonths, setAgeMonths] = useState(null);
  const [ageDays, setAgeDays] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);
  const [solutionSteps, setSolutionSteps] = useState([]);

  const handleBirthdateChange = (e) => {
    setBirthdate(e.target.value);
    setError('');
  };

  const calculateAge = () => {
    if (!birthdate) {
      setError('Please enter a birthdate.');
      return;
    }

    const birth = new Date(birthdate);
    const today = new Date();

    if (birth > today) {
      setError('Birthdate must be before today.');
      return;
    }

    let years = today.getFullYear() - birth.getFullYear();
    let months = today.getMonth() - birth.getMonth();
    let days = today.getDate() - birth.getDate();

    if (days < 0) {
      months -= 1;
      days += 30; // Assuming 30 days in a month for simplicity
    }

    if (months < 0) {
      years -= 1;
      months += 12;
    }

    setAgeYears(years);
    setAgeMonths(months);
    setAgeDays(days);

    // Update chart data
    setChartData({
      labels: ['Years', 'Months', 'Days'],
      datasets: [
        {
          label: 'Age Calculation',
          data: [years, months, days],
          backgroundColor: ['rgba(75, 192, 192, 0.5)', 'rgba(54, 162, 235, 0.5)', 'rgba(255, 206, 86, 0.5)'],
          borderColor: ['rgba(75, 192, 192, 1)', 'rgba(54, 162, 235, 1)', 'rgba(255, 206, 86, 1)'],
          borderWidth: 1
        }
      ]
    });

    // Update solution steps
    setSolutionSteps([
      `Birthdate: ${birth.toDateString()}`,
      `Today: ${today.toDateString()}`,
      `Age in Years: ${years}`,
      `Age in Months: ${months}`,
      `Age in Days: ${days}`
    ]);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    calculateAge();
  };

  return (
    <>
      <Helmet>
        <title>Age Calculator | BioLab Tools</title>
        <meta name="description" content="Calculate your age in years, months, and days based on your birthdate. Use this calculator to determine your age accurately." />
        <meta name="keywords" content="age calculator, calculate age, birthdate, age in years, age in months, age in days" />
      </Helmet>

      <div className="min-h-screen bg-gray-50 py-8">
        <div className="container mx-auto px-4 max-w-7xl">
          <header className="mb-8 text-center">
            <div className="text-4xl font-bold text-green-600 mb-2">🔍</div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-2">Age Calculator</h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Calculate your age in years, months, and days based on your birthdate. Get accurate age calculations.
            </p>
          </header>

          <div className="bg-white p-6 rounded-lg shadow-md max-w-2xl mx-auto">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Birthdate</label>
                <input
                  type="date"
                  value={birthdate}
                  onChange={handleBirthdateChange}
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Enter birthdate"
                />
              </div>

              <button
                type="submit"
                className="w-full bg-green-600 text-white py-3 px-4 rounded-md font-semibold hover:bg-green-700 transition duration-200"
              >
                Calculate Age
              </button>

              {error && (
                <div className="bg-red-100 text-red-700 p-4 rounded-md mt-4">
                  {error}
                </div>
              )}

              {ageYears !== null && (
                <div className="mt-8">
                  <h2 className="text-2xl font-bold text-gray-800 mb-6">Result</h2>
                  <div className="bg-gray-50 p-6 rounded-lg shadow-sm">
                    <p className="text-lg font-medium">Age in Years: {ageYears}</p>
                    <p className="text-lg font-medium">Age in Months: {ageMonths}</p>
                    <p className="text-lg font-medium">Age in Days: {ageDays}</p>
                  </div>

                  <div className="mt-8">
                    <h3 className="text-xl font-bold text-gray-800 mb-4">Visualization</h3>
                    <div className="h-96">
                      <Bar
                        data={chartData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          scales: {
                            y: {
                              beginAtZero: true,
                              title: { display: true, text: 'Age' }
                            },
                            x: { title: { display: true, text: 'Unit' } }
                          },
                          plugins: { title: { display: true, text: 'Age Calculation' } }
                        }}
                      />
                    </div>
                  </div>

                  <div className="mt-8">
                    <h3 className="text-xl font-bold text-gray-800 mb-4">Solution Steps</h3>
                    <div className="bg-gray-50 p-6 rounded-lg shadow-sm">
                      <ul className="list-disc pl-6 space-y-2">
                        {solutionSteps.map((step, index) => (
                          <li key={index} className="text-gray-600">{step}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </form>
          </div>

          {/* FAQ Section */}
          <div className="mt-16 bg-white p-6 rounded-lg shadow-md max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Frequently Asked Questions (FAQ)</h2>
            <div className="space-y-6">
              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">What is age calculation?</h3>
                <p className="text-gray-600">
                  Age calculation is the process of determining a person's age in years, months, and days based on their birthdate.
                </p>
              </div>

              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">How does this calculator work?</h3>
                <p className="text-gray-600">
                  This calculator uses the JavaScript `Date` object to calculate the difference between the current date and the birthdate.
                </p>
              </div>

              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Can I use this calculator for legal purposes?</h3>
                <p className="text-gray-600">
                  Yes, this calculator is accurate and suitable for legal purposes, helping you determine a person's age accurately.
                </p>
              </div>
            </div>
          </div>

          {/* How to Use Section */}
          <div className="mt-16 bg-white p-6 rounded-lg shadow-md max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">How to Use the Age Calculator</h2>
            <div className="space-y-8">
              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 1: Enter Birthdate</h3>
                <p className="text-gray-600">
                  Input your birthdate.
                </p>
              </div>

              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 2: Calculate Age</h3>
                <p className="text-gray-600">
                  Click the "Calculate Age" button to get the result.
                </p>
              </div>

              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 3: View Result and Visualization</h3>
                <p className="text-gray-600">
                  The result will be displayed, along with a bar chart visualizing the age calculation.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AgeCalculator;